/**
 * API Client Service
 * Axios-based HTTP client for backend API communication
 * Replaces Firebase Firestore calls
 */

import axios, { AxiosInstance, AxiosError } from 'axios';

// API Configuration
// Production'da window.location.origin kullan, development'ta localhost
const getApiBaseUrl = () => {
  // window objesi yoksa (SSR/build-time) fallback döndür
  if (typeof window === 'undefined') {
    return 'http://localhost:5001/api';
  }
  
  // window.location kontrolü - RUNTIME'DA HER ZAMAN KULLAN
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // Production domain kontrolü - tservis.tr için Apache reverse proxy kullan
  if (hostname === 'tservis.tr' || hostname.includes('tservis.tr')) {
    // HTTPS ise Apache reverse proxy üzerinden /api kullan (port yok)
    if (protocol === 'https:') {
      return `${window.location.origin}/api`;
    }
    // HTTP ise (geliştirme amaçlı) port 5001 kullan
    return `http://${hostname}:5001/api`;
  }
  
  // Production HTTPS (diğer domainler için)
  if (protocol === 'https:') {
    // Apache/Nginx reverse proxy kullanılıyorsa: window.location.origin + '/api'
    return `${window.location.origin}/api`;
  }
  
  // Development'ta localhost (sadece gerçekten localhost ise)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:5001/api';
  }
  
  // Diğer durumlar için (örneğin IP adresi)
  return `${protocol}//${hostname}:5001/api`;
};

const API_TIMEOUT = parseInt(process.env['REACT_APP_API_TIMEOUT'] || '30000');

// Create axios instance
// baseURL'i boş bırak, interceptor'da runtime'da dinamik olarak ekle
const apiClient: AxiosInstance = axios.create({
  baseURL: '', // Boş bırak, interceptor'da runtime'da ekle
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - Add auth token and dynamic baseURL
apiClient.interceptors.request.use(
  (config) => {
    // Runtime'da baseURL'i dinamik olarak belirle (HER ZAMAN override et)
    // ÖNEMLİ: baseURL'i her zaman override et, build-time değerlerini yok say
    const apiBaseUrl = getApiBaseUrl();
    
    // HER ZAMAN override et - build-time'da set edilmiş localhost değerlerini yok say
    config.baseURL = apiBaseUrl;
    
    // Debug log for portal endpoints
    if (config.url?.includes('/portal/')) {
      console.log('🔍 [API-CLIENT] Portal endpoint:', {
        url: config.url,
        baseURL: config.baseURL,
        fullURL: `${config.baseURL}${config.url}`,
        method: config.method
      });
    }
    
    // Public endpoints - don't send auth token
    const publicEndpoints = [
      '/auth/login',
      '/auth/register',
      '/portal/',
      '/services/public/approval',
      '/services/public/track',
      '/customers/portal/',
      '/quotes/public/'
    ];
    const isPublicEndpoint = publicEndpoints.some(endpoint => 
      config.url?.includes(endpoint)
    );
    
    if (!isPublicEndpoint) {
      // Check if this is an admin endpoint - use adminAuthToken
      const isAdminEndpoint = config.url?.startsWith('/admin/');
      
      // Debug log for role-signin
      if (config.url?.includes('/auth/role-signin')) {
        // const token = localStorage.getItem('authToken'); // Not used
      }
      
      // Get token from localStorage for authenticated endpoints
      // Admin endpoints use adminAuthToken, regular endpoints use authToken
      const token = isAdminEndpoint 
        ? localStorage.getItem('adminAuthToken')
        : localStorage.getItem('authToken');
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        // Token yoksa - public sayfada değilsek yönlendirme yapılacak
        // İsteği iptal etme, backend'e gönder ve 401 hatası gelsin
        // Response interceptor zaten bunu handle edecek
        // Bu sayede gereksiz network istekleri yapılmayacak ama hata mesajı da gösterilmeyecek
        const publicPaths = [
          '/signin', 
          '/', 
          '/register', 
          '/admin/login', 
          '/role-signin',
          '/service-approval',
          '/customer-portal',
          '/customer-portal-login',
          '/track',
          '/payment-success',
          '/payment-failure',
          '/payment-cancel',
          '/verify-email',
          /* 🔴 2026-09-06 — BUNLAR EKSİKTİ. Giriş yapmamış ziyaretçi halka açık
             bir sayfada API çağrısı yapan bir bileşene denk gelince aşağıdaki
             `window.location.href = '/signin'` tetikleniyor ve sayfadan
             ATILIYORDU. Somut vaka: /pricing, `subscriptionAPI.getActivePlans()`
             çağırıyor; istek gönderilmeden önce ziyaretçi /signin'e fırlatılıyordu.
             Oysa `GET /api/subscriptions/plans` backend'de ZATEN public (200
             dönüyor) — sorun tamamen bu listede. Sayfa sitemap'te ve robots.txt
             Allow listesinde olduğu için Google da giriş ekranını görüyordu. */
          '/pricing',
          '/features',
          '/docs',
          '/about',
          '/status',
          '/privacy',
          '/gdpr',
          '/distance-selling',
          '/delivery-return',
          '/quote',
          '/wo',
          '/is-emri'
        ];
        const isPublicPath = publicPaths.some(path => 
          window.location.pathname === path || window.location.pathname.startsWith(path + '/')
        );
        
        if (!isPublicPath) {
          // Public sayfada değiliz ve token yok - sessizce yönlendir
          if (isAdminEndpoint) {
            window.location.href = '/admin/login';
          } else {
            window.location.href = '/signin';
          }
          // İsteği iptal et - yönlendirme yapıldı
          return Promise.reject(new Error('Authentication required'));
        }
      }
    } else {
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * 🔑 SESSİZ OTURUM YENİLEME
 *
 * 🔴 SORUN: Herhangi bir 401'de uygulama refreshToken'ı KULLANMAK YERİNE SİLİP giriş
 *    ekranına atıyordu. JWT ömrü 7 gün olduğu için kullanıcı en geç haftada bir
 *    (ve her geçici 401'de) yeniden giriş yapmak zorunda kalıyordu.
 *
 * ÇÖZÜM: 401 gelince önce `/auth/refresh-token` denenir; backend hem yeni token hem
 * YENİ refresh token döner (dönen pencere), yani uygulamayı 30 gün içinde bir kez bile
 * açan kullanıcı bir daha giriş istemez. Sadece yenileme de başarısız olursa çıkış yapılır.
 *
 * Aynı anda birden çok istek 401 alırsa TEK yenileme çağrısı yapılır (`yenilemeSozu`),
 * diğerleri onu bekler — yoksa her istek ayrı yenileme tetikleyip birbirinin token'ını
 * geçersiz kılardı.
 */
let yenilemeSozu: Promise<string | null> | null = null;

const sessizYenile = async (): Promise<string | null> => {
  const refresh = localStorage.getItem('refreshToken');
  if (!refresh) return null;
  if (yenilemeSozu) return yenilemeSozu;

  yenilemeSozu = (async () => {
    try {
      // Not: apiClient DEĞİL düz axios — interceptor döngüsüne girmesin.
      const r = await axios.post(`${getApiBaseUrl()}/auth/refresh-token`, { refreshToken: refresh });
      const d: any = r?.data?.data;
      if (r?.data?.success && d?.token) {
        saveAuthToken(d.token, d.refreshToken);
        return d.token as string;
      }
      return null;
    } catch {
      return null;
    } finally {
      // Bir sonraki 401'de yeniden denenebilsin
      setTimeout(() => { yenilemeSozu = null; }, 0);
    }
  })();

  return yenilemeSozu;
};

// Response interceptor - Handle errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    // --- 401: önce oturumu sessizce yenilemeyi dene, çıkış SON çare
    const istek: any = error.config || {};
    if (error.response?.status === 401 && !istek.__yenilemeDenendi) {
      const yol = String(istek.url || '');
      // Girişin kendisi 401 verdiyse yenilemeye çalışmak anlamsız (şifre yanlış demektir)
      if (!/\/auth\/(login|refresh-token|role-signin)/.test(yol)) {
        istek.__yenilemeDenendi = true;
        const yeni = await sessizYenile();
        if (yeni) {
          istek.headers = { ...(istek.headers || {}), Authorization: `Bearer ${yeni}` };
          return apiClient.request(istek);
        }
      }
    }

    if (error.response) {
      // Server responded with error
      const status = error.response.status;
      
      switch (status) {
        case 401:
          // Unauthorized - clear token and redirect to login
          // But don't redirect if we're on public pages (register, signin, landing, admin)
          const publicPaths = [
            '/signin', 
            '/', 
            '/register', 
            '/admin/login',
            '/service-approval',
            '/customer-portal',
            '/customer-portal-login',
            '/track',
            '/payment-success',
            '/payment-failure',
            '/payment-cancel',
            '/verify-email'
          ];
          const isPublicPath = publicPaths.some(path => 
            window.location.pathname === path || window.location.pathname.startsWith(path + '/')
          );
          
          // Check if we're on admin page
          const isAdminPath = window.location.pathname.startsWith('/admin');
          
          // Check if we're on role-signin page - don't clear token here, let the page handle it
          const isRoleSignInPath = window.location.pathname === '/role-signin' || window.location.pathname.startsWith('/role-signin');
          
          // Token yoksa veya geçersizse - sadece console'da sessizce logla, gereksiz yönlendirme yapma
          const hasToken = localStorage.getItem('authToken') || localStorage.getItem('adminAuthToken');
          if (!hasToken && !isPublicPath && !isRoleSignInPath) {
            // Token yok ve public sayfada değiliz - sessizce yönlendir
            if (isAdminPath) {
              window.location.href = '/admin/login';
            } else {
              removeAuthToken();
              localStorage.removeItem('companies');
              localStorage.removeItem('selectedCompany');
              localStorage.removeItem('selectedRole');
              localStorage.removeItem('selectedPersonnelId');
              localStorage.removeItem('selectedUserId');
              localStorage.removeItem('refreshToken');
              window.location.href = '/signin';
            }
            // Hata mesajını console'a yazdırma - zaten yönlendiriyoruz
            return Promise.reject(error);
          }
          
          // If on admin page, only clear adminUser, not regular user data
          if (isAdminPath) {
            localStorage.removeItem('adminAuthToken');
            localStorage.removeItem('adminUser');
            // Redirect to admin login page
            if (!isPublicPath) {
              window.location.href = '/admin/login';
            }
          } else if (!isRoleSignInPath) {
            // Regular user - clear all auth data (but not on role-signin page)
            removeAuthToken();
            localStorage.removeItem('companies');
            localStorage.removeItem('selectedCompany');
            localStorage.removeItem('selectedRole');
            localStorage.removeItem('selectedPersonnelId');
            localStorage.removeItem('selectedUserId');
            localStorage.removeItem('refreshToken');
            // Redirect to signin page only if not on a public page
            if (!isPublicPath) {
              window.location.href = '/signin';
            }
          }
          // If on role-signin page, don't clear token - let the page handle the error
          break;
        
        case 403:
          // Forbidden
          break;
        
        case 404:
          // Not found
          break;
        
        case 500:
          // Server error
          break;
        
        case 502:
        case 503:
        case 504:
          // Service Unavailable, Bad Gateway, Gateway Timeout
          // Silently handle - don't log to console
          // These are usually temporary server/database issues
          break;
      }
    } else if (error.request) {
      // Request made but no response
    } else {
      // Request setup error
    }
    
    return Promise.reject(error);
  }
);

// ==========================================
// AUTH API
// ==========================================

export interface RegisterData {
  email: string;
  password: string;
  display_name?: string;
  company_name?: string;
  phone_number?: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  data: {
    user: any;
    companies?: any[];
    token: string;
    refreshToken: string;
  };
  message?: string;
}

export const authAPI = {
  register: (data: RegisterData) => 
    apiClient.post<AuthResponse>('/auth/register', data),
  
  login: (data: LoginData) => 
    apiClient.post<AuthResponse>('/auth/login', data),
  
  logout: () => 
    apiClient.post('/auth/logout'),
  
  getCurrentUser: () => 
    apiClient.get('/auth/me'),
  
  roleSignIn: (data: { personnel_id?: string; user_id?: string; role_password: string }) =>
    apiClient.post('/auth/role-signin', data),
  
  getCompanyUsers: () =>
    apiClient.get('/auth/company-users'),
  
  verifyEmail: (token: string) =>
    apiClient.get(`/auth/verify-email/${token}`),
  
  resendVerificationEmail: (email: string) =>
    apiClient.post('/auth/resend-verification', { email }),
  
  changePassword: (data: { oldPassword: string; newPassword: string }) =>
    apiClient.post('/auth/change-password', data),
  
  verifyPassword: (password: string) =>
    apiClient.post('/auth/verify-password', { password }),

  updateProfile: (data: { displayName?: string; phoneNumber?: string; photoUrl?: string }) =>
    apiClient.put('/auth/profile', data),

  uploadUserPhoto: (data: { image: string; imageType?: string }) =>
    apiClient.post('/auth/upload-photo', data)
};

// ==========================================
// CUSTOMER API
// ==========================================

export interface CustomerData {
  name: string;
  surname?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  district?: string;
  customer_type?: 'bireysel' | 'kurumsal' | 'tedarikci';
  balance_try?: number;
  balance_usd?: number;
  notes?: string;
  [key: string]: any;
}

export interface CustomerListParams {
  debtFilter?: 'withDebt' | 'noDebt'; // ✅ P1-2 FIX: filterDebt backend'e gönderilmeli
  search?: string;
  type?: string;
  status?: string;
  /** Virgülle ayrılmış müşteri id listesi */
  ids?: string;
  city?: string;
  district?: string;
  tags?: string;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export const customerAPI = {
  getAll: (params?: CustomerListParams) => 
    apiClient.get('/customers', { params }),
  
  getById: (id: string) => 
    apiClient.get(`/customers/${id}`),

  getNextCode: () => {
    return apiClient.get('/customers/next-code');
  },
  
  create: (data: CustomerData) => 
    apiClient.post('/customers', data),
  
  bulkImport: (customers: CustomerData[]) => 
    apiClient.post('/customers/bulk-import', { customers }),
  
  update: (id: string, data: Partial<CustomerData>) => 
    apiClient.put(`/customers/${id}`, data),
  
  delete: (id: string, permanent: boolean = false) => 
    apiClient.delete(`/customers/${id}`, { params: { permanent } }),

  // Portal login endpoints (public, no auth required)
  findCustomerByPhone: (phone: string) =>
    apiClient.post('/customers/portal/find-by-phone', { phone }),

  findCustomerByToken: (token: string) =>
    apiClient.post('/customers/portal/find-by-token', { token }),

  sendSMSPasswordToCustomer: (customerId: string, companyId: string, phoneNumber: string) =>
    apiClient.post('/customers/portal/send-sms-password', { customerId, companyId, phoneNumber }),

  /** 📍 Müşterinin kayıtlı konumu (2026-08-30). */
  konumGetir: (id: string) =>
    apiClient.get(`/customers/${id}/konum`),

  /** 📍 Konumu yazar. `enlem`/`boylam` null gönderilirse konum SİLİNİR. */
  konumKaydet: (id: string, veri: {
    enlem: number | null;
    boylam: number | null;
    dogrulukM?: number | null;
    not?: string;
  }) => apiClient.put(`/customers/${id}/konum`, veri)
};

// ==========================================
// SMS LOG API
export interface SMSLogSummaryResponse {
  success: boolean;
  data: {
    totalSMS: number;
    successfulSMS: number;
    failedSMS: number;
    totalCredits: number;
    successRate: number;
  };
}
// ==========================================

export interface SmsLogData {
  customer_id?: string;
  phone_number: string;
  message: string;
  message_type?: string;
  status?: string;
  error_message?: string;
  credits_used?: number;
  provider_response?: any;
}

export const smsLogAPI = {
  create: (data: SmsLogData) =>
    apiClient.post('/sms-logs', data),
  
  getAll: (params?: {
    customer_id?: string;
    status?: string;
    message_type?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }) =>
    apiClient.get('/sms-logs', { params }),
  
  getSummary: (params?: {
    customer_id?: string;
    status?: string;
    message_type?: string;
    start_date?: string;
    end_date?: string;
  }) =>
    apiClient.get<SMSLogSummaryResponse>('/sms-logs/summary', { params }),
  
  getById: (id: string) =>
    apiClient.get(`/sms-logs/${id}`),
  
  update: (id: string, data: Partial<SmsLogData>) =>
    apiClient.put(`/sms-logs/${id}`, data),
  
  send: (data: {
    phoneNumbers: string[];
    message: string;
    header?: string;
    customer_id?: string;
    message_type?: string;
  }) =>
    apiClient.post('/sms-logs/send', data)
};

// ==========================================
// BULK SMS RECIPIENT API
// ==========================================

export interface BulkSmsRecipientData {
  customer_id: string;
  phone: string;
  customer_code?: string;
  customer_name?: string;
  balance_try?: number;
}

// ==========================================
// SMS GROUP API
// ==========================================

export const smsGroupAPI = {
  getAll: () =>
    apiClient.get('/sms-groups'),

  create: (data: { name: string; description?: string; color?: string; icon?: string; filters?: any }) =>
    apiClient.post('/sms-groups', data),

  update: (id: string, data: any) =>
    apiClient.put(`/sms-groups/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/sms-groups/${id}`),

  getMembers: (id: string) =>
    apiClient.get(`/sms-groups/${id}/members`),

  previewCount: (filters: any) =>
    apiClient.post('/sms-groups/preview-count', { filters }),

  getFilterOptions: () =>
    apiClient.get('/sms-groups/filter-options')
};

export const bulkSmsRecipientAPI = {
  add: (recipients: BulkSmsRecipientData[]) =>
    apiClient.post('/bulk-sms-recipients/add', { recipients }),
  
  getAll: (params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }) =>
    apiClient.get('/bulk-sms-recipients', { params }),
  
  clear: (status?: string) =>
    apiClient.delete('/bulk-sms-recipients/clear', { params: { status } }),
  
  remove: (id: string) =>
    apiClient.delete(`/bulk-sms-recipients/${id}`),
  
  updateStatus: (id: string, status: 'sent' | 'failed' | 'skipped', errorMessage?: string) =>
    apiClient.put(`/bulk-sms-recipients/${id}/status`, { status, error_message: errorMessage })
};

// ==========================================
// TECHNICAL SERVICE API
// ==========================================

export const technicalServiceAPI = {
  getAll: (params?: any) => 
    apiClient.get('/services', { params }),
  
  getById: (id: string) => 
    apiClient.get(`/services/${id}`),
  
  getNextCode: () => {
    return apiClient.get('/services/next-code');
  },

  // Public: token ile iş emri görüntüleme (giriş gerektirmez)
  getPublicView: (token: string) =>
    apiClient.get(`/services/public/view/${token}`),

  create: (data: any) =>
    apiClient.post('/services', data),
  
  update: (id: string, data: any) => 
    apiClient.put(`/services/${id}`, data),
  
  updateStatus: (id: string, status: string, notes?: string) => 
    apiClient.patch(`/services/${id}/status`, { status, notes }),
  
  delete: (id: string) => 
    apiClient.delete(`/services/${id}`)
};

// ==========================================
// TRANSACTION API (Coming Soon)
// ==========================================

export const transactionAPI = {
  getAll: (params?: any) => 
    apiClient.get('/transactions', { params }),
  
  getById: (id: string) => 
    apiClient.get(`/transactions/${id}`),
  
  getOpenDebts: (customerId: string, currency?: string) =>
    apiClient.get(`/transactions/open-debts/${customerId}`, { params: { currency } }),
  
  getAllocations: (transactionId: string) =>
    apiClient.get(`/transactions/${transactionId}/allocations`),
  
  create: (data: any) => 
    apiClient.post('/transactions', data),
  
  update: (id: string, data: any) => 
    apiClient.put(`/transactions/${id}`, data),
  
  delete: (id: string, data?: { deletion_reason?: string }) => 
    apiClient.delete(`/transactions/${id}`, { data }),

  // Portal endpoints (public, no auth required)
  getPortalTransactions: (customerId: string, companyId: string, currency: string = 'TRY', limit: number = 100) =>
    apiClient.get('/transactions/portal/transactions', { 
      params: { customerId, companyId, currency, limit } 
    }),

  getPortalCustomer: (customerId: string, companyId: string) =>
    apiClient.get(`/transactions/portal/customer/${customerId}`, { 
      params: { companyId } 
    }),

  // Deleted transactions (read-only, info only)
  getDeleted: (params?: {
    customerId?: string;
    type?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) =>
    apiClient.get('/deleted-transactions', { params })
};

// ==========================================
// STOCK API (Coming Soon)
// ==========================================

export const purchaseOrderAPI = {
  getAll: (params?: any) => apiClient.get('/purchase-orders', { params }),
  getById: (id: string) => apiClient.get(`/purchase-orders/${id}`),
  create: (data: any) => apiClient.post('/purchase-orders', data),
  update: (id: string, data: any) => apiClient.put(`/purchase-orders/${id}`, data),
  delete: (id: string) => apiClient.delete(`/purchase-orders/${id}`),
  addPendingPart: (serviceId: string, data: any) => apiClient.post(`/services/${serviceId}/parts/pending`, data)
};

// --- ALIŞ FATURASI MODÜLÜ (29.07.2026) ---
// `purchases`/`suppliers` tabloları şemada vardı ama uç yoktu; sayfalar boş kabuktu.
// NOT: `purchaseOrderAPI` (sipariş) BAŞKA bir modüldür — bu "mal geldi, stok arttı".
export const supplierAPI = {
  getAll: (params?: any) => apiClient.get('/suppliers', { params }),
  getById: (id: string) => apiClient.get(`/suppliers/${id}`),
  create: (data: any) => apiClient.post('/suppliers', data),
  update: (id: string, data: any) => apiClient.put(`/suppliers/${id}`, data),
  delete: (id: string) => apiClient.delete(`/suppliers/${id}`)
};

export const purchaseAPI = {
  getAll: (params?: any) => apiClient.get('/purchases', { params }),
  getById: (id: string) => apiClient.get(`/purchases/${id}`),
  create: (data: any) => apiClient.post('/purchases', data),
  delete: (id: string) => apiClient.delete(`/purchases/${id}`)
};

export const stockAPI = {
  getAll: (params?: any) => 
    apiClient.get('/stock', { params }),
  
  getById: (id: string) => 
    apiClient.get(`/stock/${id}`),
  
  getNextCode: () => {
    return apiClient.get('/stock/next-code');
  },
  
  getNextBarcode: () => {
    return apiClient.get('/stock/next-barcode');
  },
  
  uploadImage: (imageBase64: string) => 
    apiClient.post('/stock/upload-image', { image: imageBase64 }),
  
  create: (data: any) => 
    apiClient.post('/stock', data),
  
  update: (id: string, data: any) => 
    apiClient.put(`/stock/${id}`, data),
  
  delete: (id: string) => 
    apiClient.delete(`/stock/${id}`),

  /** 📊 Gerçek hareket geçmişi (2026-08-30). Önceden ekranda sabit
   *  "hareket yok" yazıyordu; hiç sorgu atılmıyordu. */
  getMovements: (id: string, limit = 200) =>
    apiClient.get(`/stock/${id}/movements`, { params: { limit } }),

  /** Silme öncesi: bu kart kaç satış/alış/teklif/serviste geçiyor. */
  getUsage: (id: string) =>
    apiClient.get(`/stock/${id}/usage`),

  /** Silinemeyen kart için doğru yol: pasife al (geçmiş korunur). */
  setActive: (id: string, aktif: boolean) =>
    apiClient.patch(`/stock/${id}/active`, { aktif })
};

// ==========================================
// WAREHOUSE API
// ==========================================

export const warehouseAPI = {
  getAll: (params?: any) => 
    apiClient.get('/warehouses', { params }),
  
  getById: (id: string) => 
    apiClient.get(`/warehouses/${id}`),
  
  create: (data: any) => 
    apiClient.post('/warehouses', data),
  
  update: (id: string, data: any) => 
    apiClient.put(`/warehouses/${id}`, data),
  
  delete: (id: string) => 
    apiClient.delete(`/warehouses/${id}`)
};

// ==========================================
// PAYMENT METHOD API
// ==========================================

export const paymentMethodAPI = {
  getAll: (params?: { isActive?: boolean | string }) =>
    apiClient.get('/payment-methods', { params }),

  getById: (id: string) =>
    apiClient.get(`/payment-methods/${id}`),

  create: (data: any) =>
    apiClient.post('/payment-methods', data),

  update: (id: string, data: any) =>
    apiClient.put(`/payment-methods/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/payment-methods/${id}`)
};

// ==========================================
// INVOICE NUMBERING API
// ==========================================

export const invoiceNumberingAPI = {
  generate: (type?: string, year?: number) =>
    apiClient.get('/invoice-numbering/generate', {
      params: { type, year }
    })
};

// ==========================================
// SALES API
// ==========================================

export const salesAPI = {
  getAll: (params?: any) => 
    apiClient.get('/sales', { params }),
  
  getById: (id: string) => 
    apiClient.get(`/sales/${id}`),
  
  create: (data: any) => 
    apiClient.post('/sales', data),
  
  getNextInvoiceNumber: () => 
    apiClient.get('/sales/next-invoice-number')
};

// ==========================================
// QUOTE API
// ==========================================

export const quoteAPI = {
  getAll: (params?: any) => 
    apiClient.get('/quotes', { params }),
  
  getById: (id: string) => 
    apiClient.get(`/quotes/${id}`),
  
  getPublic: (token: string) => 
    apiClient.get(`/quotes/public/${token}`),
  
  approvePublic: (token: string) => 
    apiClient.post(`/quotes/public/${token}/approve`),
  
  rejectPublic: (token: string, data?: { rejection_reason?: string }) => 
    apiClient.post(`/quotes/public/${token}/reject`, data),
  
  create: (data: any) => 
    apiClient.post('/quotes', data),
  
  update: (id: string, data: any) => 
    apiClient.put(`/quotes/${id}`, data),
  
  delete: (id: string) => 
    apiClient.delete(`/quotes/${id}`),
  
  send: (id: string) => 
    apiClient.post(`/quotes/${id}/send`),
  
  approve: (id: string) => 
    apiClient.post(`/quotes/${id}/approve`),
  
  reject: (id: string, data?: { rejection_reason?: string }) => 
    apiClient.post(`/quotes/${id}/reject`, data),
  
  convertToSale: (id: string, data?: any) => 
    apiClient.post(`/quotes/${id}/convert-to-sale`, data),
  
  // Quote Standards
  getStandards: () =>
    apiClient.get('/quotes/standards/list'),
  
  createStandard: (data: { term_text: string; is_active?: boolean; sort_order?: number }) =>
    apiClient.post('/quotes/standards', data),
  
  updateStandard: (id: string, data: { term_text?: string; is_active?: boolean; sort_order?: number }) =>
    apiClient.put(`/quotes/standards/${id}`, data),
  
  deleteStandard: (id: string) =>
    apiClient.delete(`/quotes/standards/${id}`),
  
  bulkUpdateStandards: (standards: Array<{ id: string; term_text?: string; is_active?: boolean; sort_order?: number }>) =>
    apiClient.put('/quotes/standards/bulk/update', { standards })
};

// ==========================================
// STOCK MOVEMENT API
// ==========================================

export const stockMovementAPI = {
  getAll: (params?: any) => 
    apiClient.get('/stock-movements', { params }),
  
  getById: (id: string) => 
    apiClient.get(`/stock-movements/${id}`),
  
  create: (data: any) => 
    apiClient.post('/stock-movements', data)
};

// ==========================================
// USER PREFERENCE API
// ==========================================

export const userPreferenceAPI = {
  getAll: () => 
    apiClient.get('/user-preferences'),
  
  getByKey: (key: string) => 
    apiClient.get('/user-preferences', { params: { key } }),
  
  set: (key: string, value: any) => 
    apiClient.post('/user-preferences', { key, value }),
  
  delete: (key: string) => 
    apiClient.delete(`/user-preferences/${key}`)
};

// ==========================================
// FEEDBACK API
// ==========================================

export const feedbackAPI = {
  create: (data: {
    type: 'bug' | 'feature' | 'other';
    title: string;
    description: string;
    user_id?: string;
    user_email?: string;
    status?: string;
  }) => 
    apiClient.post('/feedback', data),
  
  getAll: (params?: {
    status?: string;
    type?: string;
    priority?: string;
    limit?: number;
    offset?: number;
  }) => 
    apiClient.get('/feedback', { params }),
  
  getMyFeedbacks: () => 
    apiClient.get('/feedback/my/list'),
  
  getById: (id: string) => 
    apiClient.get(`/feedback/${id}`),
  
  update: (id: string, data: {
    status?: string;
    priority?: string;
    admin_notes?: string;
    admin_reply?: string;
  }) => 
    apiClient.put(`/feedback/${id}`, data),
  
  delete: (id: string) => 
    apiClient.delete(`/feedback/${id}`)
};

export const notificationAPI = {
  getAll: (params?: { type?: string; is_read?: boolean; priority?: string; limit?: number; offset?: number }) => 
    apiClient.get('/notifications', { params }),
  
  checkNew: (params?: { last_check_time?: string; last_notification_id?: string }) => 
    apiClient.get('/notifications/check-new', { params }),
  
  getNew: (params?: { last_check_time?: string; limit?: number }) => 
    apiClient.get('/notifications/new', { params }),
  
  getById: (id: string) => 
    apiClient.get(`/notifications/${id}`),
  
  create: (data: {
    title: string;
    message: string;
    type?: string;
    priority?: string;
    url?: string;
    reference_id?: string;
    reference_type?: string;
    target_user_id?: string;
  }) => apiClient.post('/notifications', data),
  
  markAsRead: (id: string) => 
    apiClient.patch(`/notifications/${id}/read`),
  
  markAllAsRead: () => 
    apiClient.patch('/notifications/mark-all-read'),
  
  delete: (id: string) => 
    apiClient.delete(`/notifications/${id}`),
  
  clearAll: () => 
    apiClient.delete('/notifications')
};

// ==========================================
// TRANSFER SLIP API
// ==========================================

export const transferSlipAPI = {
  getAll: (params?: any) => 
    apiClient.get('/transfer-slips', { params }),
  
  getById: (id: string) => 
    apiClient.get(`/transfer-slips/${id}`),
  
  getNextCode: () => 
    apiClient.get('/transfer-slips/next-code'),
  
  create: (data: any) => 
    apiClient.post('/transfer-slips', data),
  
  update: (id: string, data: any) => 
    apiClient.put(`/transfer-slips/${id}`, data),
  
  delete: (id: string) => 
    apiClient.delete(`/transfer-slips/${id}`)
};

// ==========================================
// SERIAL NUMBER API
// ==========================================

export const serialNumberAPI = {
  create: (data: { serial_numbers: any[] }) => 
    apiClient.post('/serial-numbers', data),
  
  getBySale: (saleId: string) => 
    apiClient.get(`/serial-numbers/sale/${saleId}`),
  
  getByStock: (stockItemId: string) => 
    apiClient.get(`/serial-numbers/stock/${stockItemId}`),
  
  search: (params?: any) => 
    apiClient.get('/serial-numbers/search', { params }),
  
  getDetail: (params?: { serial_number: string }) => 
    apiClient.get('/serial-numbers/detail', { params })
};

// ==========================================
// CATEGORY API (Saha Servisi Kategorileri)
// ==========================================

export const categoryAPI = {
  getAll: (params?: any) => 
    apiClient.get('/categories', { params }),
  
  getById: (id: string) => 
    apiClient.get(`/categories/${id}`),
  
  create: (data: any) => 
    apiClient.post('/categories', data),
  
  update: (id: string, data: any) => 
    apiClient.put(`/categories/${id}`, data),
  
  delete: (id: string) => 
    apiClient.delete(`/categories/${id}`),
  
  toggle: (id: string, isActive: boolean) => 
    apiClient.patch(`/categories/${id}/toggle`, { is_active: isActive })
};

// ==========================================
// DEVICE CATEGORY API (Cihaz Kategorileri)
// ==========================================

export const deviceCategoryAPI = {
  getAll: (params?: any) => 
    apiClient.get('/device-categories', { params }),
  
  getById: (id: string) => 
    apiClient.get(`/device-categories/${id}`),
  
  create: (data: any) => 
    apiClient.post('/device-categories', data),
  
  update: (id: string, data: any) => 
    apiClient.put(`/device-categories/${id}`, data),
  
  delete: (id: string) => 
    apiClient.delete(`/device-categories/${id}`),
  
  toggle: (id: string, isActive: boolean) => 
    apiClient.patch(`/device-categories/${id}/toggle`, { is_active: isActive })
};

// ==========================================
// SYSTEM SETTINGS API
// ==========================================

export const systemSettingsAPI = {
  get: (key?: string) => 
    apiClient.get('/system-settings', { params: key ? { key } : {} }),
  
  set: (key: string, value: any) => 
    apiClient.post('/system-settings', { setting_key: key, setting_value: value }),
  
  update: (key: string, value: any) => 
    apiClient.put('/system-settings', { setting_key: key, setting_value: value }),
  
  delete: (key: string) => 
    apiClient.delete(`/system-settings/${key}`)
};

// ==========================================
// EXPORT API
// ==========================================

export const exportAPI = {
  exportCustomers: () => 
    apiClient.get('/export/customers', { responseType: 'blob' }),
  
  exportStocks: () => 
    apiClient.get('/export/stocks', { responseType: 'blob' })
};

// ==========================================
// PERSONNEL API
// ==========================================

export const personnelAPI = {
  getAll: (params?: { 
    isActive?: boolean | string; 
    search?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }) => 
    apiClient.get('/personnel', { params }),
  
  getById: (id: string) => 
    apiClient.get(`/personnel/${id}`),
  
  create: (data: any) => 
    apiClient.post('/personnel', data),
  
  update: (id: string, data: any) => 
    apiClient.put(`/personnel/${id}`, data),
  
  delete: (id: string) => 
    apiClient.delete(`/personnel/${id}`)
};

// ==========================================
// PERSONNEL SALARY PAYMENT API
// ==========================================

export const personnelSalaryPaymentAPI = {
  getAll: (params?: {
    personnelId?: string;
    period?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }) =>
    apiClient.get('/personnel-salary-payments', { params }),
  
  getById: (id: string) =>
    apiClient.get(`/personnel-salary-payments/${id}`),
  
  create: (data: any) =>
    apiClient.post('/personnel-salary-payments', data),
  
  update: (id: string, data: any) =>
    apiClient.put(`/personnel-salary-payments/${id}`, data),
  
  delete: (id: string) =>
    apiClient.delete(`/personnel-salary-payments/${id}`),

  deductAdvances: (id: string, data: { period?: string }) =>
    apiClient.post(`/personnel-salary-payments/${id}/deduct-advances`, data)
};

// ==========================================
// PERSONNEL ADVANCE PAYMENT API
// ==========================================

export const personnelAdvancePaymentAPI = {
  getAll: (params?: {
    personnelId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }) =>
    apiClient.get('/personnel-advance-payments', { params }),
  
  getById: (id: string) =>
    apiClient.get(`/personnel-advance-payments/${id}`),
  
  create: (data: any) =>
    apiClient.post('/personnel-advance-payments', data),
  
  update: (id: string, data: any) =>
    apiClient.put(`/personnel-advance-payments/${id}`, data),
  
  delete: (id: string) =>
    apiClient.delete(`/personnel-advance-payments/${id}`),

  cancel: (id: string) =>
    apiClient.patch(`/personnel-advance-payments/${id}/cancel`)
};

// ==========================================
// PERSONNEL ATTENDANCE (PUANTAJ) API
// ==========================================

export const personnelAttendanceAPI = {
  getAll: (params?: {
    personnelId?: string;
    month?: string; // YYYY-MM
    startDate?: string;
    endDate?: string;
  }) =>
    apiClient.get('/personnel-attendance', { params }),

  getSummary: (params: { month: string; personnelId?: string }) =>
    apiClient.get('/personnel-attendance/summary', { params }),

  upsert: (data: {
    personnel_id: string;
    work_date: string;
    status: string;
    overtime_hours?: number;
    note?: string;
  }) =>
    apiClient.post('/personnel-attendance', data),

  bulkUpsert: (records: Array<{
    personnel_id: string;
    work_date: string;
    status: string;
    overtime_hours?: number;
    note?: string;
  }>) =>
    apiClient.post('/personnel-attendance/bulk', { records }),

  delete: (id: string) =>
    apiClient.delete(`/personnel-attendance/${id}`)
};

// ==========================================
// WORK ORDER API
// ==========================================

export const workOrderAPI = {
  getAll: (params?: { 
    status?: string; 
    assignedTo?: string;
    customerId?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }) =>
    apiClient.get('/work-orders', { params }),

  // Public: token ile iş emri görüntüleme (giriş gerektirmez)
  getPublicView: (token: string) =>
    apiClient.get(`/work-orders/public/view/${token}`),

  // Public: token ile iş emri aksiyonu (tamamlandı / sorun var) - giriş gerektirmez
  completePublic: (token: string, workOrderId?: string, action: 'completed' | 'problem' = 'completed', note?: string) =>
    apiClient.post(`/work-orders/public/view/${token}/complete`, { workOrderId, action, note }),

  getById: (id: string) => 
    apiClient.get(`/work-orders/${id}`),
  
  create: (data: any) => 
    apiClient.post('/work-orders', data),
  
  update: (id: string, data: any) => 
    apiClient.put(`/work-orders/${id}`, data),
  
  delete: (id: string) => 
    apiClient.delete(`/work-orders/${id}`)
};

// ==========================================
// MODULE API
// ==========================================

export const moduleAPI = {
  getAll: (params?: {
    isActive?: boolean;
    isVisible?: boolean;
    moduleType?: string;
    requiresSubscription?: boolean;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }) =>
    apiClient.get('/modules', { params }),
  
  getById: (id: string) =>
    apiClient.get(`/modules/${id}`),
  
  create: (data: any) =>
    apiClient.post('/modules', data),
  
  update: (id: string, data: any) =>
    apiClient.put(`/modules/${id}`, data),
  
  delete: (id: string) =>
    apiClient.delete(`/modules/${id}`),
  
  toggleStatus: (id: string) =>
    apiClient.patch(`/modules/${id}/toggle`)
};

// ==========================================
// CASH BOX API
// ==========================================

export const cashBoxAPI = {
  getAll: (params?: {
    isActive?: boolean | string;
  }) =>
    apiClient.get('/cash-boxes', { params }),
  
  getById: (id: string) =>
    apiClient.get(`/cash-boxes/${id}`),
  
  create: (data: any) =>
    apiClient.post('/cash-boxes', data),
  
  update: (id: string, data: any) =>
    apiClient.put(`/cash-boxes/${id}`, data),
  
  delete: (id: string) =>
    apiClient.delete(`/cash-boxes/${id}`)
};

export const cashMovementAPI = {
  getAll: (params?: {
    cashRegisterId?: string;
    movementType?: 'income' | 'expense' | 'transfer' | 'all';
    referenceType?: string | 'all';
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }) =>
    apiClient.get('/cash-movements', { params }),
  
  getById: (id: string) =>
    apiClient.get(`/cash-movements/${id}`),
  
  create: (data: {
    amount: number;
    currency?: string;
    movementType?: 'income' | 'expense';
    paymentMethod: string;
    cashRegisterId?: string;
    referenceType?: string;
    referenceId?: string;
    description?: string;
    movementDate?: string; // ✅ Tarih alanı eklendi
  }) =>
    apiClient.post('/cash-movements', data),
  
  getSummary: (params?: {
    cashRegisterId?: string;
    startDate?: string;
    endDate?: string;
  }) =>
    apiClient.get('/cash-movements/summary', { params })
};

// ==========================================
// CASH WITHDRAWAL API
// ==========================================

/** Tek Kasa sayfası (13.09.2026) — defter sunucuda hesaplanır. */
export const kasaAPI = {
  ozet: (params: { kasaId?: string; bas?: string; bit?: string }) =>
    apiClient.get('/kasa/ozet', { params }),
  gelir: (data: { kasaId: string; tutar: number; aciklama: string }) =>
    apiClient.post('/kasa/gelir', data),
  transfer: (data: { kaynakId: string; hedefId: string; tutar: number; aciklama?: string }) =>
    apiClient.post('/kasa/transfer', data),
  sayim: (data: { kasaId: string; sayilan: number; aciklama?: string }) =>
    apiClient.post('/kasa/sayim', data),
  ters: (hareketId: string, aciklama?: string) =>
    apiClient.post(`/kasa/ters/${hareketId}`, { aciklama }),
};

export const cashWithdrawalAPI = {
  getAll: (params?: {
    cash_register_id?: string;
    startDate?: string;
    endDate?: string;
  }) =>
    apiClient.get('/cash-withdrawals', { params }),
  
  create: (data: {
    amount: number;
    currency?: string;
    cash_register_id: string;
    purpose?: string;
    category?: string;
    description?: string;
    withdrawal_date?: string;
  }) =>
    apiClient.post('/cash-withdrawals', data)
};

// ==========================================
// EXPENSE API
// ==========================================

export const expenseAPI = {
  getAll: (params?: {
    category?: string;
    startDate?: string;
    endDate?: string;
    paymentMethod?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }) =>
    apiClient.get('/expenses', { params }),
  
  getById: (id: string) =>
    apiClient.get(`/expenses/${id}`),
  
  create: (data: any) =>
    apiClient.post('/expenses', data),
  
  update: (id: string, data: any) =>
    apiClient.put(`/expenses/${id}`, data),
  
  delete: (id: string) =>
    apiClient.delete(`/expenses/${id}`),
  
  getCategories: () =>
    apiClient.get('/expenses/categories'),
  
  createCategory: (data: { category_name: string; category_code?: string }) =>
    apiClient.post('/expenses/categories', data),
  
  updateCategory: (id: string, data: { category_name?: string; category_code?: string; is_active?: boolean }) =>
    apiClient.put(`/expenses/categories/${id}`, data),
  
  deleteCategory: (id: string) =>
    apiClient.delete(`/expenses/categories/${id}`)
};

// ==========================================
// ADMIN API
// ==========================================

export const adminAPI = {
  // Authentication
  login: (email: string, password: string) =>
    apiClient.post('/admin/login', { email, password }),

  // Dashboard
  getDashboardStats: () =>
    apiClient.get('/admin/dashboard-stats'),
  
  getRecentUsers: (limit?: number) =>
    apiClient.get('/admin/recent-users', { params: { limit } }),
  
  // Users Management
  getAllUsers: (params?: {
    search?: string;
    status?: string;
    subscription?: string;
    limit?: number;
    offset?: number;
  }) =>
    apiClient.get('/admin/users', { params }),
  
  getUserDetails: (id: string) =>
    apiClient.get(`/admin/users/${id}`),

  getCompanyStats: (companyId: string) =>
    apiClient.get(`/admin/companies/${companyId}/stats`),
  getCompanyCustomers: (companyId: string) =>
    apiClient.get(`/admin/companies/${companyId}/customers`),
  getCompanyStock: (companyId: string) =>
    apiClient.get(`/admin/companies/${companyId}/stock`),
  getCompanyServices: (companyId: string) =>
    apiClient.get(`/admin/companies/${companyId}/services`),
  getCompanySales: (companyId: string) =>
    apiClient.get(`/admin/companies/${companyId}/sales`),
  getCompanyTransactions: (companyId: string) =>
    apiClient.get(`/admin/companies/${companyId}/transactions`),

  updateUser: (id: string, data: any) =>
    apiClient.put(`/admin/users/${id}`, data),
  
  deleteUser: (id: string) =>
    apiClient.delete(`/admin/users/${id}`),
  
  // SMS Packages Management
  getAllSmsPackages: (params?: { isActive?: boolean }) =>
    apiClient.get('/admin/sms-packages', { params }),
  
  createSmsPackage: (data: {
    package_name: string;
    credit_amount: number;
    price: number;
    currency?: string;
    validity_days?: number;
    is_active?: boolean;
  }) =>
    apiClient.post('/admin/sms-packages', data),
  
  updateSmsPackage: (id: string, data: {
    package_name?: string;
    credit_amount?: number;
    price?: number;
    currency?: string;
    validity_days?: number;
    is_active?: boolean;
  }) =>
    apiClient.put(`/admin/sms-packages/${id}`, data),
  
  deleteSmsPackage: (id: string) =>
    apiClient.delete(`/admin/sms-packages/${id}`),
  
  // Subscription Plans Management
  getAllSubscriptionPlans: (params?: { isActive?: boolean }) =>
    apiClient.get('/admin/subscription-plans', { params }),
  
  createSubscriptionPlan: (data: {
    plan_name: string;
    plan_code: string;
    price: number;
    currency?: string;
    billing_period: 'monthly' | 'yearly';
    max_users?: number;
    max_customers?: number;
    max_services?: number;
    features?: object;
    is_active?: boolean;
  }) =>
    apiClient.post('/admin/subscription-plans', data),
  
  updateSubscriptionPlan: (id: string, data: {
    plan_name?: string;
    plan_code?: string;
    price?: number;
    currency?: string;
    billing_period?: 'monthly' | 'yearly';
    max_users?: number;
    max_customers?: number;
    max_services?: number;
    features?: object;
    is_active?: boolean;
  }) =>
    apiClient.put(`/admin/subscription-plans/${id}`, data),
  
  deleteSubscriptionPlan: (id: string) =>
    apiClient.delete(`/admin/subscription-plans/${id}`),

  // Purchase Reports
  getPurchaseReports: (params?: {
    startDate?: string;
    endDate?: string;
    purchaseType?: 'sms' | 'subscription';
    status?: string;
    companyId?: string;
    page?: number;
    limit?: number;
  }) =>
    apiClient.get('/admin/purchase-reports', { params }),
};

// SMS Packages API (Public)
export const smsPackageAPI = {
  getActivePackages: () =>
    apiClient.get('/sms-packages')
};

// Subscription Plans API (Public)
export const subscriptionAPI = {
  getActivePlans: () =>
    apiClient.get('/subscriptions/plans')
};

// Iyzico Payment API
export const iyzicoAPI = {
  // Initialize SMS payment
  initializePayment: (data: { packageId: string; callbackUrl: string }) =>
    apiClient.post('/iyzico/initialize', data),
  
  // Initialize subscription payment
  initializeSubscriptionPayment: (data: { planId: string; callbackUrl: string }) =>
    apiClient.post('/iyzico/initialize-subscription', data),
  
  // Get purchase ID from token
  getPurchaseByToken: (token: string) =>
    apiClient.get(`/iyzico/purchase-by-token/${encodeURIComponent(token)}`),
  
  // Verify payment status
  verifyPayment: (purchaseId: string) =>
    apiClient.get(`/iyzico/verify/${purchaseId}`),
  
  // Get purchase history
  getPurchaseHistory: () =>
    apiClient.get('/iyzico/purchase-history')
};

// Dashboard API
/**
 * Uyarı Merkezi — 2026-08-29'da wa-beyin'den ürüne taşındı.
 * Firma kimliği sunucuda oturumdan okunuyor, istemci hiçbir kimlik göndermiyor.
 */
/**
 * İnternet paket uzatma köprüsü — Filikanet'e özel.
 * Modül kapalı firmalarda sunucu 404 döner.
 */
export const miyAPI = {
  /**
   * `aboneId` verilmezse: tek hatlı caride o hat döner, ÇOK HATLIDA `secimGerekli:true`
   * + `hatlar` döner ve MİY'e hiç gidilmez (ekran seçtirmeli).
   */
  getPaket: (customerId: string, aboneId?: string) =>
    apiClient.get(`/miy/paket/${customerId}`, aboneId ? { params: { aboneId } } : undefined),
  /**
   * 🔴 Çok hatlı caride `aboneId` ZORUNLU — sunucu tahmin etmez, 400 döner.
   * Yanlış hattı uzatmak geri alınamaz bir MİY işlemi.
   */
  uzat: (customerId: string, ay: number, aboneId?: string) =>
    apiClient.post('/miy/uzat', { customerId, ay, ...(aboneId ? { aboneId } : {}) }),
  /** `deneme:true` → hiçbir şey yazılmaz, sadece ne yapılacağı raporlanır. */
  /** Eşitlemeyi BAŞLATIR, hemen döner. `tam:true` abone verisini de tazeler (~22 dk). */
  esitle: (deneme = false, tam = false) =>
    apiClient.post('/miy/esitle', { deneme, tam }),
  /** Arka plandaki işin durumu — ekran saniyede bir yoklar. */
  esitleDurum: () => apiClient.get('/miy/esitle-durum'),
};

export const alertsAPI = {
  getAll: () => apiClient.get('/alerts'),
};

export const dashboardAPI = {
  getMetrics: () => apiClient.get('/dashboard/metrics'),
  getRecentServices: (params?: { limit?: number }) =>
    apiClient.get('/dashboard/recent-services', { params }),
  getWorkOrdersMetrics: () => apiClient.get('/dashboard/work-orders-metrics'),
  getFazlaOdemeler: () => apiClient.get('/dashboard/fazla-odemeler'),

  // Users Management
  getAllUsers: (params?: {
    search?: string;
    status?: string;
    subscription?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }) =>
    apiClient.get('/admin/users', { params }),
  
  getUserDetails: (id: string) =>
    apiClient.get(`/admin/users/${id}`),
  
  updateUser: (id: string, data: {
    isActive?: boolean;
    subscriptionPlan?: string;
    subscriptionExpiresAt?: string;
    addDays?: number;
    addSmsCredits?: number;
  }) =>
    apiClient.put(`/admin/users/${id}`, data),
  
  deleteUser: (id: string) =>
    apiClient.delete(`/admin/users/${id}`),

  // Company Modules Management
  getCompanyModules: (companyId: string) =>
    apiClient.get(`/admin/users/${companyId}/modules`),
  
  updateCompanyModule: (companyId: string, moduleId: string, data: {
    is_enabled?: boolean;
    is_visible?: boolean;
    custom_name?: string;
    custom_icon?: string;
    sort_order?: number;
    access_level?: string;
  }) =>
    apiClient.put(`/admin/users/${companyId}/modules/${moduleId}`, data),
  
  bulkUpdateCompanyModules: (companyId: string, modules: Array<{
    module_id: string;
    is_enabled?: boolean;
    is_visible?: boolean;
    custom_name?: string;
    custom_icon?: string;
    sort_order?: number;
    access_level?: string;
  }>) =>
    apiClient.put(`/admin/users/${companyId}/modules`, { modules })
};

export const exchangeRateAPI = {
  getLatest: (currencies?: string[]) =>
    apiClient.get('/exchange-rates/latest', {
      params: currencies ? { currencies: currencies.join(',') } : {}
    }),
  getByDate: (date: string, currencies?: string[]) =>
    apiClient.get('/exchange-rates', {
      params: {
        date,
        ...(currencies ? { currencies: currencies.join(',') } : {})
      }
    }),
  createOrUpdate: (data: {
    base_currency?: string;
    target_currency: string;
    rate: number;
    rate_date?: string;
    source?: string;
  }) => apiClient.post('/exchange-rates', data),
};

// ==========================================
// PERMISSION API
// ==========================================

export interface PermissionCheckResponse {
  success: boolean;
  hasPermission: boolean;
  reason?: string;
  error?: string;
}

export interface UserPermissionsResponse {
  success: boolean;
  data: {
    permissions: string[];
    role: string;
    isSuperAdmin?: boolean;
  };
  error?: string;
}

export const permissionAPI = {
  check: (permission: string, selectedRole?: string | null, selectedPersonnelId?: string | null) =>
    apiClient.get<PermissionCheckResponse>('/permissions/check', {
      params: { 
        permission,
        ...(selectedRole && { selectedRole }),
        ...(selectedPersonnelId && { selectedPersonnelId })
      }
    }),
  
  getAll: () =>
    apiClient.get<UserPermissionsResponse>('/permissions')
};

// ==========================================
// HELPER FUNCTIONS
// ==========================================

/**
 * Cookie utility functions
 */
const setCookie = (name: string, value: string, days: number = 1): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  const isSecure = window.location.protocol === 'https:';
  const secureFlag = isSecure ? '; Secure' : '';
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Strict${secureFlag}`;
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + '=';
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    if (!cookie) continue;
    let trimmedCookie = cookie.trim();
    if (trimmedCookie.indexOf(nameEQ) === 0) {
      return trimmedCookie.substring(nameEQ.length);
    }
  }
  return null;
};

const deleteCookie = (name: string): void => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

/**
 * Save auth token to localStorage and optionally to cookie
 */
export const saveAuthToken = (token: string, refreshToken?: string): void => {
  // Save to localStorage
  localStorage.setItem('authToken', token);
  
  // Optionally save to cookie (for better security and cross-tab sync)
  // Note: For HTTP-only cookies, backend should set them via Set-Cookie header
  setCookie('authToken', token, 1); // 1 day
  
  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken);
    // 🔴 7 gündü: çerez token'dan önce ölünce kullanıcı yeniden giriş yapmak zorunda kalıyordu
    setCookie('refreshToken', refreshToken, 365);
  }
};

/**
 * Remove auth token from localStorage and cookies
 */
export const removeAuthToken = (): void => {
  // Clear localStorage
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  localStorage.removeItem('companies');
  localStorage.removeItem('selectedCompany');
  localStorage.removeItem('selectedRole');
  localStorage.removeItem('selectedPersonnelId');
  localStorage.removeItem('selectedUserId');
  localStorage.removeItem('refreshToken');
  
  // Clear cookies
  deleteCookie('authToken');
  deleteCookie('refreshToken');
};

/**
 * Get auth token from localStorage or cookie
 */
export const getAuthToken = (): string | null => {
  // First try localStorage
  const token = localStorage.getItem('authToken');
  if (token) return token;
  
  // Fallback to cookie if localStorage is empty
  const cookieToken = getCookie('authToken');
  if (cookieToken) {
    // Restore to localStorage for consistency
    localStorage.setItem('authToken', cookieToken);
    return cookieToken;
  }
  
  return null;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

// Visitor Statistics API (Admin only)
export const visitorStatsAPI = {
  getStats: (params?: { startDate?: string; endDate?: string; groupBy?: string; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.groupBy) queryParams.append('groupBy', params.groupBy);
    if (params?.limit) queryParams.append('limit', String(params.limit));
    return apiClient.get(`/admin/visitor-stats?${queryParams.toString()}`);
  },
  getSummary: (params?: { startDate?: string; endDate?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    return apiClient.get(`/admin/visitor-stats/summary?${queryParams.toString()}`);
  },
  getDetailed: (params?: { startDate?: string; endDate?: string; ip?: string; limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.ip) queryParams.append('ip', params.ip);
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.offset) queryParams.append('offset', String(params.offset));
    return apiClient.get(`/admin/visitor-stats/detailed?${queryParams.toString()}`);
  }
};

// Export default client
// ==========================================
// BARCODE CATALOG API
// ==========================================

export const barcodeCatalogAPI = {
  searchByBarcode: (barcode: string) =>
    apiClient.get('/barcode-catalog/search', { params: { barcode } }),
  
  searchByName: (query: string, limit: number = 20) =>
    apiClient.get('/barcode-catalog/search-name', { params: { q: query, limit } }),
  
  batchSearch: (barcodes: string[]) =>
    apiClient.post('/barcode-catalog/batch-search', { barcodes }),
  
  getStats: () =>
    apiClient.get('/barcode-catalog/stats')
};

// ==========================================
// ALL TRANSACTIONS API
// ==========================================

export const allTransactionsAPI = {
  getAll: (params?: { 
    page?: number; 
    limit?: number; 
    sort?: string; 
    order?: string;
    startDate?: string;
    endDate?: string;
  }) =>
    apiClient.get('/all-transactions', { params }),
  
  delete: (id: string, documentType: string) =>
    apiClient.delete(`/all-transactions/${id}`, { params: { document_type: documentType } })
};

export default apiClient;



/**
 * 📊 Ölü rapor sayfalarının gerçek verisi (2026-08-30).
 * Bu sayfalar yazılmıştı ama hiç veri çekmiyordu; uçlar için bkz.
 * backend/controllers/reportsExtraController.ts
 */
export const raporAPI = {
  personelPerformansi: (baslangic?: string, bitis?: string) =>
    apiClient.get('/reports/personnel-performance', { params: { baslangic, bitis } }),

  servisDurumu: (baslangic?: string, bitis?: string) =>
    apiClient.get('/reports/service-status', { params: { baslangic, bitis } }),

  musteriAnalizi: (baslangic?: string, bitis?: string) =>
    apiClient.get('/reports/customer-analysis', { params: { baslangic, bitis } }),
};


/**
 * 🚚 SAHA SERVİSİ — ziyaret akışı (2026-08-30).
 * Randevu → Yola Çıktı → Yerinde → Sonuç. Bkz.
 * backend/controllers/sahaServisController.ts
 */
export const sahaAPI = {
  /** Açık saha işleri (durum='tumu' ile kapananlar da gelir). */
  liste: (durum: 'acik' | 'tumu' = 'acik') =>
    apiClient.get('/services/saha/liste', { params: { durum } }),

  yolaCik: (id: string) =>
    apiClient.post(`/services/${id}/saha/yola-cik`, {}),

  /** Konum gönderilirse işin YAPILDIĞI yer kaydedilir. */
  yerinde: (id: string, konum?: { enlem: number; boylam: number }) =>
    apiClient.post(`/services/${id}/saha/yerinde`, konum || {}),

  /** Ziyareti sonuçlandırır; 'cozuldu' ise kapıda fatura keser. */
  sonuc: (id: string, veri: {
    sonuc: 'cozuldu' | 'parca-gerekli' | 'atolyeye-alindi' | 'musteri-yok' | 'iptal';
    not?: string;
    yeniRandevu?: string;
    kalemler?: Array<{ stockId?: string | null; ad: string; adet: number; birimFiyat: number; kdvOrani?: number }>;
    iscilik?: number;
    yolUcreti?: number;
    odemeTipi?: 'nakit' | 'kart' | 'havale' | 'veresiye';
    /** 🎁 Sorun bizden kaynaklıysa: parça stoktan düşer, %100 iskonto, tahsilat yok. */
    ucretsiz?: boolean;
    ucretsizSebep?: string;
  }) => apiClient.post(`/services/${id}/saha/sonuc`, veri),

  /** 📲 Atanan teknisyene WhatsApp'tan iş bağlantısı gönderir/hazırlar. */
  bildir: (id: string, veri?: { telefon?: string; otomatik?: boolean }) =>
    apiClient.post(`/services/${id}/saha/bildir`, veri || {}),

  /** 📏 Müşteri konumundan km ve yol ücreti önerisi. */
  mesafe: (customerId: string, kmUcreti = 10) =>
    apiClient.get('/services/saha/mesafe', { params: { customerId, kmUcreti } }),

  /** 📊 Personelin günlük saha çıktısı: ziyaret, ücretli/ücretsiz, kazanç. */
  performans: (tarih?: string) =>
    apiClient.get('/services/saha/performans', { params: tarih ? { tarih } : {} }),

  /** 🗑️ Saha ziyaretini siler — yalnız yetkili; faturalanmış kayıt reddedilir. */
  sil: (id: string) => apiClient.delete(`/services/${id}/saha`),

  isyeriKonumGetir: () => apiClient.get('/services/saha/isyeri-konum'),
  isyeriKonumKaydet: (enlem: number, boylam: number) =>
    apiClient.put('/services/saha/isyeri-konum', { enlem, boylam }),
};


/**
 * 🔧 DIŞ SERVİS — Gönder → Takip → Geri Al (2026-08-30).
 * Dış servisin aldığı ücret müşteriye AYNEN yansıtılır (kullanıcı kararı);
 * `musteriyeYansit: false` denirse maliyeti biz üstleniriz ve kârdan düşer.
 */
export const disServisAPI = {
  liste: () => apiClient.get('/services/dis-servis/liste'),

  gonder: (id: string, veri: {
    servisAdi: string; gonderimTarihi?: string; tahminiDonus?: string; not?: string;
  }) => apiClient.post(`/services/${id}/dis-servis/gonder`, veri),

  geriAl: (id: string, veri: {
    sonuc: 'onarildi' | 'onarilamadi';
    ucret: number; yapilanIslem?: string; donusTarihi?: string; musteriyeYansit?: boolean;
  }) => apiClient.post(`/services/${id}/dis-servis/geri-al`, veri),
};
